import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { Envelope } from '../stripeFeesQuery/types';
import { responseHandler } from '../utils';
import {
  OrgAiLimits,
  ResetOrgAiUsageParams,
  ResetOrgAiUsageResult,
  SetOrgAiLimitsParams,
} from './types';

/**
 * Budżety AI organizacji. Zapis wyłącznie przez backend (`verifyAdminAccess`) —
 * limit decyduje o tym, ile klient może wygenerować kosztu u dostawcy, więc nie
 * może dać się ustawić z poziomu klienta.
 */

export const ORG_AI_LIMITS_QUERY_KEY = 'org-ai-limits';

export const useGetOrgAiLimits = (
  orgId: string | undefined,
): UseQueryResult<OrgAiLimits, AxiosError> => useQuery<OrgAiLimits, AxiosError>({
  queryKey: [ORG_AI_LIMITS_QUERY_KEY, orgId],
  enabled: !!orgId,
  retry: 0,
  queryFn: async () => {
    const response = await axiosInstance.get<Envelope<OrgAiLimits>>(
      '/v3-getOrgAiLimits',
      { params: { orgId } },
    );
    return responseHandler(response).data;
  },
});

export const useSetOrgAiLimits = (): UseMutationResult<void, AxiosError, SetOrgAiLimitsParams> => useMutation<
  void,
  AxiosError,
  SetOrgAiLimitsParams
>({
  mutationFn: async (params) => {
    const response = await axiosInstance.post('/v3-setOrgAiLimits', params);
    return responseHandler(response);
  },
});

export const useResetOrgAiUsage = (): UseMutationResult<
  ResetOrgAiUsageResult,
  AxiosError,
  ResetOrgAiUsageParams
> => useMutation<ResetOrgAiUsageResult, AxiosError, ResetOrgAiUsageParams>({
  mutationFn: async (params) => {
    const response = await axiosInstance.post<Envelope<ResetOrgAiUsageResult>>(
      '/v3-resetOrgAiUsage',
      params,
    );
    return responseHandler(response).data;
  },
});
